import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Target, CircleDot } from "lucide-react";
import { ExternalRoundStats } from "@/hooks/useCourseStats";
import { Progress } from "@/components/ui/progress";

interface HoleDetail {
  hole_number: number;
  par: number;
  score: number;
  putts: number;
  fairway_hit: boolean;
  fairway_position: string | null;
  green_in_regulation: boolean;
  scrambling: boolean;
  sand_save: boolean;
  bunker: boolean;
}

// Cutoff date: stats scrambling/sand_save only from Dec 18, 2025
const STATS_CUTOFF_DATE = '2025-12-18';

interface AdvancedStatsCardsProps {
  stats: ExternalRoundStats[];
}

const AdvancedStatsCards = ({ stats }: AdvancedStatsCardsProps) => {
  const { t } = useTranslation('course-stats');

  // Calculate missed fairway tendencies from hole_details (only when fairway_hit === false)
  const missedFairwayTendencies = stats.reduce(
    (acc, s) => {
      if (s.hole_details && Array.isArray(s.hole_details)) {
        (s.hole_details as HoleDetail[]).forEach((hole) => {
          // Only count tendencies when fairway is missed
          if (hole.fairway_hit === false && hole.fairway_position) {
            if (hole.fairway_position === 'left') acc.left++;
            else if (hole.fairway_position === 'right') acc.right++;
          }
        });
      }
      return acc;
    },
    { left: 0, right: 0 }
  );

  const totalMissedFairways = missedFairwayTendencies.left + missedFairwayTendencies.right;

  // Filter stats from cutoff date for scrambling and sand save
  const statsFromCutoff = stats.filter(s => s.round_date >= STATS_CUTOFF_DATE);

  // Scrambling: % = (scrambling TRUE / GIR FALSE) × 100, only from cutoff
  const scramblingSuccesses = statsFromCutoff.reduce((acc, s) => acc + (s.scrambling_made ?? 0), 0);
  const totalHolesPlayed = statsFromCutoff.reduce((acc, s) => acc + (s.holes_played ?? 0), 0);
  const totalGIR = statsFromCutoff.reduce((acc, s) => acc + (s.greens_in_regulation ?? 0), 0);
  const missedGIRs = totalHolesPlayed - totalGIR;
  const scramblingPercentage = missedGIRs > 0 ? (scramblingSuccesses / missedGIRs) * 100 : 0;

  // Sand save: bunker=true is opportunity, sand_save=true is success, only from cutoff
  let sandSaveOpportunities = 0;
  let sandSaveSuccesses = 0;

  statsFromCutoff.forEach((s) => {
    if (s.hole_details && Array.isArray(s.hole_details)) {
      (s.hole_details as HoleDetail[]).forEach((hole) => {
        if (hole.bunker === true) {
          sandSaveOpportunities++;
          if (hole.sand_save === true) sandSaveSuccesses++;
        }
      });
    }
  });

  const sandSavePercentage = sandSaveOpportunities > 0
    ? (sandSaveSuccesses / sandSaveOpportunities) * 100
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Missed Fairway Tendencies */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('missedFairwayTendencies.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <ArrowLeft className="h-5 w-5 text-red-500" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('missedFairwayTendencies.left')}</span>
                  <span>{totalMissedFairways > 0 ? ((missedFairwayTendencies.left / totalMissedFairways) * 100).toFixed(1) : '0.0'}%</span>
                </div>
                <Progress value={totalMissedFairways > 0 ? (missedFairwayTendencies.left / totalMissedFairways) * 100 : 0} className="h-2" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ArrowRight className="h-5 w-5 text-orange-500" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('missedFairwayTendencies.right')}</span>
                  <span>{totalMissedFairways > 0 ? ((missedFairwayTendencies.right / totalMissedFairways) * 100).toFixed(1) : '0.0'}%</span>
                </div>
                <Progress value={totalMissedFairways > 0 ? (missedFairwayTendencies.right / totalMissedFairways) * 100 : 0} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scrambling */}
      <Card className="bg-emerald-50 border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-emerald-800">
            <Target className="h-5 w-5" />
            {t('scrambling.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="text-4xl font-bold text-emerald-700">
              {scramblingPercentage.toFixed(1)}%
            </div>
            <div className="text-sm text-emerald-600">
              {t('scrambling.value')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sand Save */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
            <CircleDot className="h-5 w-5" />
            {t('sandSave.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="text-4xl font-bold text-amber-700">
              {sandSavePercentage.toFixed(1)}%
            </div>
            <div className="text-sm text-amber-600">
              {t('sandSave.value')}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedStatsCards;
